import type { ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

export interface UserProfile {
  id: string;
  name: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  cpf: string;
  profiles: UserProfile[];
  permissions: string[];
  temporaryPassword: boolean;
  avatar?: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  bwauthTokens?: {
    accessToken: string;
    refreshToken: string;
  };
  user: User;
}

export interface MeResponse {
  user: User;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const { data } = await api.post<ApiResponse<LoginResponse>>('/auth/login', credentials);
    return data.data;
  },

  me: async (): Promise<User> => {
    const { data } = await api.get<ApiResponse<MeResponse>>('/auth/me');
    return data.data.user;
  },

  logout: async (input?: {
    token?: string | null;
    refreshToken?: string | null;
  }): Promise<void> => {
    await api.post(
      '/auth/logout',
      input?.refreshToken ? { refreshToken: input.refreshToken } : {},
      input?.token
        ? {
            headers: {
              Authorization: `Bearer ${input.token}`,
            },
          }
        : undefined,
    );
  },

  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<ApiResponse<{ message: string }>>('/auth/forgot-password', {
      email,
    });
    return { success: true, message: data.data.message };
  },

  verifyResetToken: async (token: string): Promise<{ valid: boolean }> => {
    const { data } = await api.get<ApiResponse<{ valid: boolean }>>(
      `/auth/verify-reset-token?token=${encodeURIComponent(token)}`,
    );
    return data.data;
  },

  resetPassword: async (token: string, password: string): Promise<{ success: boolean }> => {
    const { data } = await api.post<ApiResponse<{ message: string }>>('/auth/reset-password', {
      token,
      password,
    });
    return { success: data.success };
  },

  completePasswordChange: async (
    pendingAuthToken: string,
    username: string,
    password: string,
  ): Promise<{ success: boolean }> => {
    const { data } = await api.post<ApiResponse<{ message: string }>>('/auth/reset-password', {
      pendingAuthToken,
      username,
      password,
    });
    return { success: data.success };
  },

  changePassword: async (passwords: ChangePasswordData): Promise<{ success: boolean }> => {
    const { data } = await api.post<ApiResponse<{ message: string }>>(
      '/auth/change-password',
      passwords,
    );
    return { success: data.success };
  },
};
